import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

import { Subscription, firstValueFrom } from 'rxjs';
import { settledCache } from '../../shared/utils/cache-ready';
import { Person } from '../../objects/person';
import { PeopleService } from '../../service/entity-services/people.service';
import { PartyService } from '../../service/entity-services/party.service';
import { UsersService } from '../../service/entity-services/users.service';
import { CommonSheetsService } from '../../service/google-sheets/common-sheets.service';
import { PersonSheetsService } from '../../service/google-sheets/person-sheets.service';

import { PartyLabelPipe } from '../../shared/pipes/party-label.pipe';
@Component({
  selector: 'app-new-person-dialog',
  templateUrl: './new-person-dialog.component.html',
  styleUrls: ['./new-person-dialog.component.css'],
  standalone: true,
  imports: [
    PartyLabelPipe,
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatInputModule,
    MatCardModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule
  ]
})
export class NewPersonDialogComponent implements OnInit, OnDestroy {
  private dialogRef = inject(MatDialogRef<NewPersonDialogComponent>);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private peopleService = inject(PeopleService);
  private partyService = inject(PartyService);
  private usersService = inject(UsersService);
  private commonSheetsService = inject(CommonSheetsService);
  private personSheetsService = inject(PersonSheetsService);

  form: FormGroup;
  private formSubscription!: Subscription;

  currentPerson: Person = {} as Person;

  // Static field data
  prefixLabels: any[] = [];
  suffixLabels: any[] = [];
  titleLabels: any[] = [];
  functionLabels: any[] = [];
  geographicStates: any[] = [];
  departments: any[] = [];

  /**
   * The COMPLETE party/user lists, kept alongside the `filtered*` arrays that the
   * typeahead narrows as the user types. This component does not extend
   * CreateEntity, so it can't use the shared `filterParties`/`filterUsers` helpers
   * and keeps its own copies to filter against.
   */
  private allPartiesList: any[] = [];
  private allUsersList: any[] = [];

  // Filtered autocomplete lists
  filteredPrefixes: any[] = [];
  filteredSuffixes: any[] = [];
  filteredTitles: any[] = [];
  filteredFunctions: any[] = [];
  filteredStates: any[] = [];
  filteredDepartments: any[] = [];
  filteredParties: any[] = [];
  filteredUsers: any[] = [];

  saving = false;
  loading = true;

  constructor() {
    // Initialize the form
    this.form = this.fb.group({
      contactNum: [''],
      entityType: ['person'],

      // Name fields
      prefix: [''],
      firstName: ['', Validators.required],
      middleName: [''],
      lastName: ['', Validators.required],
      suffix: [''],
      salutation: [''],
      fullName: [''],
      friendlyName: [''],

      // Professional info
      title: [''],
      function: [''],
      department: [''],

      // Contact information
      email: ['', [Validators.email]],
      email2: ['', [Validators.email]],
      primaryPhone: [''],
      officePhone: [''],
      mobilePhone: [''],
      secondaryPhone: [''],
      faxPhone: [''],

      // Primary address
      address: [''],
      city: [''],
      state: [''],
      zip: [''],

      // Secondary address
      secondaryAddress: [''],
      secondaryCity: [''],
      secondaryState: [''],
      secondaryZip: [''],

      // Relationships
      primaryVendor: [''],
      primaryVendorFriendlyName: [''],
      primaryResponsibleParty: [''],

      // Arrays
      vendors: [[]],
      people: [[]],
      baas: [[]],
      cois: [[]],
      contracts: [[]],
      parties: [[]],
      attachments: [[]],
      criticalDates: [[]],
      notes: [[]],
      riskAssessments: [[]],
      vendorFriendlyNames: [[]],

      // Additional fields
      description: [''],
      other: ['']
    });
  }

  async ngOnInit() {
    this.loading = true;

    try {
      // Load static field values
      await this.getStaticFieldValues();

      // Load autocomplete data.
      //
      // settledCache, NOT firstValueFrom: allParties$ re-emits a GROWING array
      // once per page of the paginated fetch, so firstValueFrom captured page
      // one — roughly 100 of cbhi's ~4,500 parties — and never updated. The
      // picker then looked like it was excluding whole categories of party when
      // it was really just showing an arbitrary early slice.
      [this.allPartiesList, this.allUsersList] = await Promise.all([
        settledCache(this.partyService.allParties$),
        settledCache(this.usersService.allUsers$)
      ]);
      this.filteredParties = this.allPartiesList;
      this.filteredUsers = this.allUsersList;

      // Initialize new person with contactNum
      this.form.patchValue({
        contactNum: Date.now().toString()
      });

      // Subscribe to form changes
      this.formSubscription = this.form.valueChanges.subscribe(changes => {
        this.currentPerson = { ...this.currentPerson, ...changes };

        // Auto-generate fullName when name fields change
        if (changes.firstName || changes.middleName || changes.lastName) {
          this.updateFullName();
        }
      });
    } catch (error) {
      console.error('Error during initialization:', error);
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  async getStaticFieldValues() {
    try {
      this.prefixLabels = await firstValueFrom(this.personSheetsService.getPrefixLabels());
      this.filteredPrefixes = [...this.prefixLabels];

      this.suffixLabels = await firstValueFrom(this.personSheetsService.getSuffixLabels());
      this.filteredSuffixes = [...this.suffixLabels];

      this.titleLabels = await firstValueFrom(this.personSheetsService.getTitleLabels());
      this.filteredTitles = [...this.titleLabels];

      this.functionLabels = await firstValueFrom(this.personSheetsService.getPersonFunctionLabels());
      this.filteredFunctions = [...this.functionLabels];

      this.geographicStates = await firstValueFrom(this.commonSheetsService.getGeographicStates());
      this.filteredStates = [...this.geographicStates];

      this.departments = await firstValueFrom(this.commonSheetsService.getDepartments());
      this.filteredDepartments = [...this.departments];
    } catch (error) {
      console.error('Error loading static field values:', error);
    }
  }

  updateFullName() {
    const firstName = this.form.get('firstName')?.value || '';
    const middleName = this.form.get('middleName')?.value || '';
    const lastName = this.form.get('lastName')?.value || '';

    let fullName = firstName;
    if (middleName) fullName += ' ' + middleName;
    if (lastName) fullName += ' ' + lastName;

    this.form.patchValue({ fullName: fullName.trim() }, { emitEvent: false });
  }

  // Display functions for autocomplete
  displayUserFn(user: any): string {
    return user && user.shortFriendlyName ? user.shortFriendlyName : '';
  }

  displayPartyFn(party: any): string {
    return party && party.name ? party.name : '';
  }

  /**
   * Narrow the Primary Vendor dropdown as the user types.
   *
   * Without this the autocomplete rendered a fixed array and ignored the input
   * entirely, which is unusable against cbhi's ~4,500 parties — there is no way to
   * scroll to the one you want. The full list stays in `allPartiesList` so the
   * saved-value lookups keep resolving no matter what has been typed.
   */
  filterParties(event: any): void {
    const query = (event?.target?.value ?? '').toLowerCase();
    this.filteredParties = query
      ? this.allPartiesList.filter(p => (p?.name ?? '').toLowerCase().includes(query))
      : [...this.allPartiesList];
  }

  /**
   * Same, for the Primary Responsible Party (user) picker. Matches on
   * `friendlyName` ("First Last (email)") so typing a first name, a last name, or
   * an email all work.
   */
  filterUsers(event: any): void {
    const query = (event?.target?.value ?? '').toLowerCase();
    this.filteredUsers = query
      ? this.allUsersList.filter(u => (u?.friendlyName ?? '').toLowerCase().includes(query))
      : [...this.allUsersList];
  }

  isFormValid(): boolean {
    const requiredFields = ['firstName', 'lastName'];
    return requiredFields.every(field => this.form.get(field)?.value);
  }

  getValidationMessage(): string {
    if (!this.form.get('firstName')?.value) return 'First name is required';
    if (!this.form.get('lastName')?.value) return 'Last name is required';
    return '';
  }

  cancel() {
    this.dialogRef.close(null);
  }

  async onSubmit() {
    if (!this.isFormValid()) {
      alert(this.getValidationMessage());
      return;
    }

    this.saving = true;
    const personToSave: Person = this.form.value;

    // Ensure contactNum exists
    if (!personToSave.contactNum) {
      personToSave.contactNum = Date.now().toString();
    }

    // Ensure fullName is set
    if (!personToSave.fullName) {
      this.updateFullName();
      personToSave.fullName = this.form.get('fullName')?.value;
    }

    // Extract IDs from object fields
    if (personToSave.primaryVendor && typeof personToSave.primaryVendor === 'object') {
      personToSave.primaryVendor = (personToSave.primaryVendor as any).vendorNum || personToSave.primaryVendor;
    }

    if (personToSave.primaryResponsibleParty && typeof personToSave.primaryResponsibleParty === 'object') {
      personToSave.primaryResponsibleParty = (personToSave.primaryResponsibleParty as any).userNum || personToSave.primaryResponsibleParty;
    }

    console.log('Saving person:', personToSave);

    this.peopleService.writePerson(personToSave).subscribe({
      next: (response) => {
        console.log('Person saved successfully', response);
        this.saving = false;
        this.dialogRef.close(personToSave);
      },
      error: (err) => {
        console.error('Failed to save person:', err);
        this.saving = false;
        alert('Failed to save person. Please try again.');
      }
    });
  }
}
